/**
 * @file TestRunPanel.jsx
 * @description The Testing tab's live run — the whole page, read top to
 *              bottom the way the bench crew reads it:
 *
 *                TOP     what the website is RECEIVING right now. The TCU's
 *                        own data (GPS, battery, signal, IMU) and the
 *                        machine's data (the CAN readings) each get a column,
 *                        and only the ones the tester asked for are shown.
 *                BOTTOM  the verdict — "as per the model, has everything
 *                        arrived or not": the per-model CAN frame checklist
 *                        (machine_can_ids) with a green tick per frame that
 *                        came and a red cross per frame that didn't, under
 *                        the plain data/GPS/CAN/ignition/engine ladder.
 *
 *              A machine whose model has no protocol table — and an unmapped
 *              bench unit — is judged on "is data arriving at all", exactly
 *              the rule the service team works to.
 *
 *              Polls /api/hardware/live-check every 5s (same cadence as the
 *              installation ladder).
 *
 * @param {string}   deviceId     the TCU carrying the telemetry
 * @param {string}   equipmentId  the mapped machine, when there is one
 * @param {string[]} scopes       ['device'] | ['machine'] | both
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, Chip, Stack, CircularProgress, Alert, Link as MuiLink,
} from '@mui/material';
import {
    Power as PowerIcon,
    GpsFixed as GpsIcon,
    Key as KeyIcon,
    SettingsInputComponent as CanIcon,
    Bolt as EngineIcon,
    CheckCircle as OkIcon,
    Cancel as MissIcon,
    Sensors as DataIcon,
    RemoveCircleOutline as NotStoredIcon,
    BatteryFull as BatteryIcon,
    NetworkCell as SignalIcon,
    ScreenRotation as ImuIcon,
    PrecisionManufacturing as MachineIcon,
    Memory as ChipIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import CanCheckPanel from './CanCheckPanel';
import { formatToIST } from '../../../utils/dataUtils';

const POLL_MS = 5000;

const fmtT = (ts) => {
    if (!ts) return '';
    const out = formatToIST(ts, 'HH:mm:ss');
    return out === 'N/A' || out === 'Invalid Date' ? '' : out;
};

/** The TCU's own checks — what a DEVICE test is asking about. */
const DEVICE_STEPS = [
    { key: 'gps', label: 'GPS fix', icon: <GpsIcon /> },
    { key: 'battery', label: 'Device battery', icon: <BatteryIcon /> },
    { key: 'signal', label: 'Network signal', icon: <SignalIcon /> },
    { key: 'imu', label: 'IMU data', icon: <ImuIcon /> },
];

/** The machine's checks — what a MACHINE test is asking about. */
const MACHINE_STEPS = [
    { key: 'can', label: 'CAN data', icon: <CanIcon /> },
    { key: 'ignition', label: 'Ignition sensed', icon: <KeyIcon /> },
    { key: 'engine', label: 'Engine running seen', icon: <EngineIcon /> },
];

/** One reading line — key, value, when it last arrived. */
const ReadingRow = ({ r }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5,
        borderBottom: '1px dashed', borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
    }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {r.key}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
            {String(r.value)}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, minWidth: 52, textAlign: 'right' }}>
            {fmtT(r.at)}
        </Typography>
    </Box>
);

/**
 * A tick/cross list of checks.
 * Three states, not two: green when it arrived, RED when it should have and
 * didn't, and GREY when the platform never keeps that reading (the IMU —
 * ingest drops acc/gyr/ang). A grey line is not a fault on the bench, so it
 * must not read as one, and it is not counted as a miss.
 */
const CheckList = ({ steps, checks }) => (
    <Stack spacing={1.25}>
        {steps.map(step => {
            const c = checks[step.key] || {};
            const grey = !c.ok && c.not_stored;
            const colour = c.ok ? '#16a34a' : grey ? '#94a3b8' : '#dc2626';
            return (
                <Box key={step.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    {c.ok
                        ? <OkIcon sx={{ color: colour, fontSize: 20, mt: '1px' }} />
                        : grey
                            ? <NotStoredIcon sx={{ color: colour, fontSize: 20, mt: '1px' }} />
                            : <MissIcon sx={{ color: colour, fontSize: 20, mt: '1px' }} />}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {React.cloneElement(step.icon, { sx: { fontSize: 15, color: colour } })}
                            <Typography variant="body2" fontWeight={700}
                                sx={{ color: c.ok ? 'text.primary' : grey ? 'text.secondary' : 'error.main' }}>
                                {step.label}
                            </Typography>
                            {c.ok && c.at && (
                                <Typography variant="caption" color="text.disabled">{fmtT(c.at)}</Typography>
                            )}
                            {grey && <Chip size="small" label="not stored" sx={{ height: 17, fontSize: 10, fontWeight: 700 }} />}
                        </Stack>
                        <Typography variant="caption" sx={{ color: c.ok ? 'text.secondary' : grey ? 'text.disabled' : '#b45309' }}>
                            {c.ok ? (c.value || 'OK') : (c.hint || 'Missing')}
                        </Typography>
                    </Box>
                </Box>
            );
        })}
    </Stack>
);

const TestRunPanel = ({ deviceId, equipmentId, scopes = ['device', 'machine'], children }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!deviceId) return;
        try {
            const r = await hardwareLifecycleService.liveCheck(deviceId);
            setData(r);
            setError('');
        } catch (e) {
            setError(e.message || 'Live check failed');
        } finally {
            setLoading(false);
        }
    }, [deviceId]);

    useEffect(() => {
        setLoading(true);
        setData(null);
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    if (!deviceId) return null;
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>;
    }
    if (error) return <Alert severity="error">{error}</Alert>;

    const wantDevice = scopes.includes('device');
    const wantMachine = scopes.includes('machine');

    const device = data?.device || null;
    const checks = data?.checks || {};
    const deviceChecks = data?.device_checks || {};
    const readings = data?.readings || [];
    const deviceReadings = data?.device_readings || [];
    const eqId = equipmentId || device?.equipment_id || null;

    // "All green" counts only what this test asked for.
    const steps = [
        ...(wantDevice ? DEVICE_STEPS.map(s => ({ ...s, from: deviceChecks })) : []),
        ...(wantMachine ? MACHINE_STEPS.map(s => ({ ...s, from: checks })) : []),
    ];
    // A reading the platform never keeps (IMU) is neither green nor a miss —
    // it is out of the tally entirely, so "4/4 green" still means all good.
    const scored = steps.filter(s => !s.from[s.key]?.not_stored);
    const greens = scored.filter(s => s.from[s.key]?.ok).length;

    const machineLine = device && (device.machine_brand || device.machine_model || device.machine_serial)
        ? [device.machine_brand, device.machine_model, device.machine_serial].filter(Boolean).join(' · ')
        : null;

    // Both columns when both data sets were asked for; the one column fills the
    // width when only one was.
    const cols = wantDevice && wantMachine
        ? { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1fr)' }
        : { xs: '1fr' };

    return (
        <Box>
            {!data?.registered && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    This device id is not in the database — frames still show here, but register it before installing.
                </Alert>
            )}
            {machineLine && (
                <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 0.5 }}>
                    Machine: <strong>{machineLine}</strong> — the model checks below are for this make &amp; model.
                </Alert>
            )}

            {/* ── TOP — what is actually arriving ─────────────────────────── */}
            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <DataIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={800}>Receiving data</Typography>
                    </Stack>
                    <Chip size="small"
                        label={`${data?.frames_in_window || 0} frame(s) / ${data?.window_min || 15} min · refreshes 5s`}
                        color={data?.frames_in_window ? 'success' : 'default'}
                        sx={{ fontWeight: 700, height: 22 }} />
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 2, alignItems: 'start' }}>
                    {wantDevice && (
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                                <ChipIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" fontWeight={800} color="text.secondary"
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                    Device data — GPS · battery · signal · IMU
                                </Typography>
                            </Stack>
                            {deviceReadings.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                    Nothing from the TCU in the last {data?.window_min || 15} minutes — power it up and wait for it to join the network.
                                </Typography>
                            ) : (
                                <Stack spacing={0} sx={{ maxHeight: 340, overflowY: 'auto' }}>
                                    {deviceReadings.map(r => <ReadingRow key={`d-${r.key}`} r={r} />)}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {wantMachine && (
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                                <MachineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" fontWeight={800} color="text.secondary"
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                    Machine data — {readings.length} reading(s)
                                </Typography>
                            </Stack>
                            {readings.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                    No machine readings in the last {data?.window_min || 15} minutes — key on, and check the CAN tap.
                                </Typography>
                            ) : (
                                <Stack spacing={0} sx={{ maxHeight: 340, overflowY: 'auto' }}>
                                    {readings.map(r => <ReadingRow key={`m-${r.key}`} r={r} />)}
                                </Stack>
                            )}
                        </Box>
                    )}
                </Box>
            </Card>

            {/* ── BOTTOM — has everything the model should send arrived? ──── */}
            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={800}>
                        Received or not — {deviceId}
                    </Typography>
                    <Chip size="small" label={`${greens}/${scored.length} green`}
                        color={greens === scored.length ? 'success' : greens > 0 ? 'warning' : 'error'}
                        sx={{ fontWeight: 700, height: 22 }} />
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 2, alignItems: 'start' }}>
                    {wantDevice && (
                        <Box>
                            <Typography variant="caption" fontWeight={800} color="text.secondary"
                                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Device
                            </Typography>
                            <CheckList steps={DEVICE_STEPS} checks={deviceChecks} />
                        </Box>
                    )}
                    {wantMachine && (
                        <Box>
                            <Typography variant="caption" fontWeight={800} color="text.secondary"
                                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Machine
                            </Typography>
                            <CheckList steps={[{ key: 'connected', label: 'Data receiving', icon: <PowerIcon /> }, ...MACHINE_STEPS]} checks={checks} />
                        </Box>
                    )}
                </Box>

                {wantMachine && !checks.can?.ok && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        CAN mode and RAW filters are set from{' '}
                        <MuiLink href="/device-config" underline="hover">Device Config</MuiLink>
                        {' '}(SHOW CAN CFG · SET CANM RAW|J1939).
                    </Typography>
                )}
            </Card>

            {/* Per make & model: the frame-by-frame CAN checklist (Dingli
                protocols in machine_can_ids). An unmapped bench unit has no
                model to check against — for it the CAN line above is the
                whole CAN verdict. */}
            {wantMachine && eqId && (
                <CanCheckPanel deviceId={deviceId} equipmentId={eqId} />
            )}

            {children}
        </Box>
    );
};

export default TestRunPanel;
